import { useState, useRef, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Pencil, Calendar, Upload, X, FileSpreadsheet, AlertCircle, CheckCircle2, ChevronDown } from 'lucide-react';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';
import api from '../utils/api';
import Modal from '../components/layout/Modal';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const DAY_STYLE = {
  Monday:    { bg: '#EEEDFE', text: '#534AB7' },
  Tuesday:   { bg: '#E6F1FB', text: '#185FA5' },
  Wednesday: { bg: '#E1F5EE', text: '#0F6E56' },
  Thursday:  { bg: '#FAEEDA', text: '#854F0B' },
  Friday:    { bg: '#EAF3DE', text: '#3B6D11' },
  Saturday:  { bg: '#FCEBEB', text: '#A32D2D' },
  Sunday:    { bg: '#FBEAF0', text: '#993556' },
};

const REQUIRED_IMPORT_COLS = ['course_code', 'day_of_week', 'time_slot_label', 'room_name'];
const ALL_IMPORT_COLS = ['course_code', 'day_of_week', 'time_slot_label', 'room_name', 'lecturer_name', 'academic_year_name', 'notes'];

/* ─── Parse a time string like "08:00 - 10:00 AM" → minutes since midnight ── */
function parseSlotMinutes(label = '') {
  // Grab the first time token, e.g. "08:00" from "08:00 - 10:00 AM"
  const match = label.match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/i);
  if (!match) return 0;
  let hours = parseInt(match[1], 10);
  const mins = parseInt(match[2], 10);
  const meridiem = match[3]?.toUpperCase();
  if (meridiem === 'PM' && hours !== 12) hours += 12;
  if (meridiem === 'AM' && hours === 12) hours = 0;
  return hours * 60 + mins;
}

/* ─── Sort entries: by DAYS order, then by slot start time ──────────────── */
function sortEntries(entries) {
  return [...entries].sort((a, b) => {
    const dayDiff = DAYS.indexOf(a.day_of_week) - DAYS.indexOf(b.day_of_week);
    if (dayDiff !== 0) return dayDiff;
    return parseSlotMinutes(a.time_slot_label) - parseSlotMinutes(b.time_slot_label);
  });
}

/* ─── shared input/label classes ─────────────────────────────────────── */
const inputCls =
  'w-full px-3 py-2.5 bg-white border border-[#E2E0D8] rounded-xl text-[13px] ' +
  'text-[#1a1a18] placeholder-[#B4B2A9] focus:outline-none focus:ring-2 ' +
  'focus:ring-[#1a1a18]/10 focus:border-[#888780] transition-colors';
const labelCls = 'block text-[11px] font-medium text-[#888780] uppercase tracking-wider mb-1.5';

/* ─── Day pill ───────────────────────────────────────────────────────── */
function DayPill({ day }) {
  const s = DAY_STYLE[day] || { bg: '#F1EFE8', text: '#5F5E5A' };
  return (
    <span
      className="inline-block px-2.5 py-1 rounded-lg text-[11px] font-medium whitespace-nowrap"
      style={{ background: s.bg, color: s.text }}
    >
      {day}
    </span>
  );
}

/* ─── Mobile card for a single entry ────────────────────────────────── */
function EntryCard({ e, onEdit, onDelete }) {
  return (
    <div className="bg-white border border-[#E2E0D8] rounded-2xl p-4 space-y-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[14px] font-semibold text-[#1a1a18] truncate">{e.course_code}</p>
          <p className="text-[12px] text-[#888780] mt-0.5 truncate">{e.course_name}</p>
        </div>
        <DayPill day={e.day_of_week} />
      </div>

      <div className="grid grid-cols-2 gap-2 text-[12px]">
        <div>
          <p className="text-[10px] font-medium text-[#B4B2A9] uppercase tracking-wider mb-0.5">Time</p>
          <p className="text-[#5F5E5A] font-medium">{e.time_slot_label}</p>
        </div>
        <div>
          <p className="text-[10px] font-medium text-[#B4B2A9] uppercase tracking-wider mb-0.5">Room</p>
          <p className="text-[#2C2C2A]">{e.room_name}</p>
          {e.building && <p className="text-[#B4B2A9] text-[11px]">{e.building}</p>}
        </div>
        <div>
          <p className="text-[10px] font-medium text-[#B4B2A9] uppercase tracking-wider mb-0.5">Lecturer</p>
          <p className="text-[#5F5E5A]">{e.lecturer_name || <span className="text-[#D3D1C7]">—</span>}</p>
        </div>
        <div>
          <p className="text-[10px] font-medium text-[#B4B2A9] uppercase tracking-wider mb-0.5">Year</p>
          <p className="text-[#888780]">{e.academic_year_name}</p>
        </div>
      </div>

      <div className="flex items-center gap-2 pt-1 border-t border-[#F1EFE8]">
        <button
          onClick={() => onEdit(e)}
          className="flex-1 flex items-center justify-center gap-1.5 py-1.5
                     text-[12px] font-medium text-[#5F5E5A] hover:text-[#1a1a18]
                     bg-[#F7F6F2] hover:bg-[#F1EFE8] rounded-lg transition-colors"
        >
          <Pencil className="w-3 h-3" /> Edit
        </button>
        <button
          onClick={() => { if (window.confirm('Delete this class?')) onDelete(e.id); }}
          className="flex-1 flex items-center justify-center gap-1.5 py-1.5
                     text-[12px] font-medium text-[#888780] hover:text-[#A32D2D]
                     bg-[#F7F6F2] hover:bg-[#FCEBEB] rounded-lg transition-colors"
        >
          <Trash2 className="w-3 h-3" /> Delete
        </button>
      </div>
    </div>
  );
}

/* ─── Page ───────────────────────────────────────────────────────────── */
export default function ClassTimetablePage() {
  const qc = useQueryClient();
  const [selectedYear, setSelectedYear] = useState('');
  const [filterDay, setFilterDay] = useState('');
  const [modal, setModal] = useState({ open: false, mode: 'create', data: null });
  const [importOpen, setImportOpen] = useState(false);

  const { data: years = [] } = useQuery(['years'], () => api.get('/academic-years').then(r => r.data), {
    onSuccess: (data) => {
      if (!selectedYear) {
        const active = data.find(y => y.is_active);
        if (active) setSelectedYear(String(active.id));
      }
    },
  });
  const { data: courses = [] } = useQuery(['courses'], () => api.get('/courses').then(r => r.data));
  const { data: rooms   = [] } = useQuery(['rooms'],   () => api.get('/rooms').then(r => r.data));
  const { data: slots   = [] } = useQuery(['slots'],   () => api.get('/time-slots').then(r => r.data));
  const { data: entries = [], isLoading } = useQuery(
    ['class-timetables', selectedYear],
    () => api.get('/class-timetables', { params: { yearId: selectedYear } }).then(r => r.data),
    { enabled: !!selectedYear }
  );

  // Filter then sort by day order + time
  const sorted = sortEntries(filterDay ? entries.filter(e => e.day_of_week === filterDay) : entries);

  const saveMutation = useMutation(
    (form) => modal.mode === 'create'
      ? api.post('/class-timetables', form)
      : api.put(`/class-timetables/${modal.data.id}`, form),
    {
      onSuccess: () => {
        qc.invalidateQueries(['class-timetables']);
        toast.success('Saved');
        setModal({ open: false });
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Failed to save'),
    }
  );

  const deleteMutation = useMutation(
    (id) => api.delete(`/class-timetables/${id}`),
    {
      onSuccess: () => { qc.invalidateQueries(['class-timetables']); toast.success('Deleted'); },
      onError: () => toast.error('Failed to delete'),
    }
  );

  const bulkMutation = useMutation(
    (rows) => api.post('/class-timetables/bulk', { entries: rows }),
    {
      onSuccess: (res) => {
        qc.invalidateQueries(['class-timetables']);
        toast.success(`${res.data?.created ?? '?'} class(es) imported`);
        setImportOpen(false);
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Bulk import failed'),
    }
  );

  return (
    <div className="min-h-screen bg-[#F7F6F2]">

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <header className="bg-[#F7F6F2] border-b border-[#E2E0D8] px-4 sm:px-8 py-4
                         flex items-center justify-between sticky top-0 z-10">
        <div>
          <p className="text-[10px] text-[#B4B2A9] uppercase tracking-[0.12em] mb-0.5 hidden sm:block">
            Timetabling System
          </p>
          <h1 className="text-[16px] sm:text-[18px] font-medium text-[#1a1a18] leading-none">
            Class Timetable
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setImportOpen(true)}
            className="flex items-center gap-1.5 px-3 py-2 bg-white border border-[#E2E0D8]
                       hover:border-[#888780] text-[#5F5E5A] hover:text-[#1a1a18]
                       text-[12px] sm:text-[13px] font-medium rounded-xl transition-colors"
          >
            <Upload className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Import</span>
          </button>
          <button
            onClick={() => setModal({ open: true, mode: 'create', data: null })}
            className="flex items-center gap-1.5 px-3 sm:px-4 py-2 bg-[#1a1a18] hover:bg-[#2C2C2A]
                       text-[#F7F6F2] text-[12px] sm:text-[13px] font-medium rounded-xl transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Schedule class</span>
            <span className="sm:hidden">Add</span>
          </button>
        </div>
      </header>

      <main className="px-4 sm:px-8 py-5 sm:py-7 max-w-7xl mx-auto">

        {/* ── Filters ─────────────────────────────────────────────────── */}
        <div className="space-y-2.5 mb-5">
          {/* Year selector + count */}
          <div className="flex items-center gap-2.5">
            <select
              value={selectedYear}
              onChange={e => setSelectedYear(e.target.value)}
              className="flex-1 sm:flex-none px-3 py-2 bg-white border border-[#E2E0D8] rounded-xl
                         text-[13px] text-[#1a1a18] focus:outline-none focus:border-[#888780] transition-colors"
            >
              <option value="">All years</option>
              {years.map(y => (
                <option key={y.id} value={y.id}>
                  {y.name}{y.is_active ? ' · Active' : ''}
                </option>
              ))}
            </select>

            {sorted.length > 0 && (
              <span className="ml-auto text-[12px] text-[#888780] whitespace-nowrap">
                {sorted.length} {sorted.length === 1 ? 'class' : 'classes'}
              </span>
            )}
          </div>

          {/* Day filter pills — horizontally scrollable on mobile */}
          <div className="flex items-center gap-1.5 overflow-x-auto pb-1 -mx-4 px-4 sm:mx-0 sm:px-0 sm:flex-wrap">
            <button
              onClick={() => setFilterDay('')}
              className={`flex-shrink-0 px-3 py-1.5 rounded-lg text-[12px] font-medium transition-colors
                ${!filterDay
                  ? 'bg-[#1a1a18] text-[#F7F6F2]'
                  : 'bg-white border border-[#E2E0D8] text-[#5F5E5A] hover:border-[#888780]'}`}
            >
              All
            </button>
            {DAYS.map(d => {
              const s = DAY_STYLE[d];
              const active = filterDay === d;
              return (
                <button
                  key={d}
                  onClick={() => setFilterDay(d === filterDay ? '' : d)}
                  className="flex-shrink-0 px-3 py-1.5 rounded-lg text-[12px] font-medium border transition-colors"
                  style={active
                    ? { background: s.bg, color: s.text, borderColor: s.text + '40' }
                    : { background: 'white', color: '#5F5E5A', borderColor: '#E2E0D8' }
                  }
                >
                  {d.slice(0, 3)}
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Mobile: Cards ────────────────────────────────────────────── */}
        <div className="sm:hidden space-y-3">
          {isLoading ? (
            <div className="text-center text-[13px] text-[#B4B2A9] py-16">Loading…</div>
          ) : sorted.length === 0 ? (
            <div className="flex flex-col items-center gap-3 py-16">
              <div className="w-10 h-10 rounded-xl bg-[#F1EFE8] flex items-center justify-center">
                <Calendar className="w-5 h-5 text-[#B4B2A9]" />
              </div>
              <p className="text-[13px] text-[#B4B2A9]">No classes scheduled yet</p>
            </div>
          ) : sorted.map(e => (
            <EntryCard
              key={e.id}
              e={e}
              onEdit={(e) => setModal({ open: true, mode: 'edit', data: e })}
              onDelete={(id) => deleteMutation.mutate(id)}
            />
          ))}
        </div>

        {/* ── Desktop: Table ───────────────────────────────────────────── */}
        <div className="hidden sm:block bg-white border border-[#E2E0D8] rounded-2xl overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#F1EFE8]">
                {['Day', 'Course', 'Time slot', 'Room', 'Lecturer', 'Year', ''].map(h => (
                  <th
                    key={h}
                    className="text-left text-[10px] font-medium text-[#888780]
                               uppercase tracking-widest px-5 py-3.5"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="text-center text-[13px] text-[#B4B2A9] py-16">
                    Loading…
                  </td>
                </tr>
              ) : sorted.length === 0 ? (
                <tr>
                  <td colSpan={7}>
                    <div className="flex flex-col items-center gap-3 py-16">
                      <div className="w-10 h-10 rounded-xl bg-[#F1EFE8] flex items-center justify-center">
                        <Calendar className="w-5 h-5 text-[#B4B2A9]" />
                      </div>
                      <p className="text-[13px] text-[#B4B2A9]">No classes scheduled yet</p>
                    </div>
                  </td>
                </tr>
              ) : sorted.map((e, i) => (
                <tr
                  key={e.id}
                  className={`group hover:bg-[#FAFAF8] transition-colors
                    ${i < sorted.length - 1 ? 'border-b border-[#F1EFE8]' : ''}`}
                >
                  <td className="px-5 py-3.5">
                    <DayPill day={e.day_of_week} />
                  </td>
                  <td className="px-5 py-3.5">
                    <p className="text-[13px] font-medium text-[#1a1a18]">{e.course_code}</p>
                    <p className="text-[11px] text-[#888780] mt-0.5">{e.course_name}</p>
                  </td>
                  <td className="px-5 py-3.5 text-[13px] text-[#5F5E5A] whitespace-nowrap">
                    {e.time_slot_label}
                  </td>
                  <td className="px-5 py-3.5">
                    <p className="text-[13px] text-[#2C2C2A]">{e.room_name}</p>
                    {e.building && (
                      <p className="text-[11px] text-[#888780] mt-0.5">{e.building}</p>
                    )}
                  </td>
                  <td className="px-5 py-3.5 text-[13px] text-[#5F5E5A]">
                    {e.lecturer_name || <span className="text-[#D3D1C7]">—</span>}
                  </td>
                  <td className="px-5 py-3.5 text-[13px] text-[#888780]">
                    {e.academic_year_name}
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center justify-end gap-1
                                    opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => setModal({ open: true, mode: 'edit', data: e })}
                        className="p-1.5 text-[#888780] hover:text-[#1a1a18]
                                   hover:bg-[#F1EFE8] rounded-lg transition-colors"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => { if (window.confirm('Delete this class?')) deleteMutation.mutate(e.id); }}
                        className="p-1.5 text-[#888780] hover:text-[#A32D2D]
                                   hover:bg-[#FCEBEB] rounded-lg transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>

      {/* ── Modals ──────────────────────────────────────────────────────── */}
      <Modal
        isOpen={modal.open}
        onClose={() => setModal({ open: false })}
        title={modal.mode === 'create' ? 'Schedule a class' : 'Edit class schedule'}
      >
        <ClassTimetableForm
          initial={modal.data}
          years={years}
          courses={courses}
          rooms={rooms}
          slots={slots}
          defaultYearId={selectedYear}
          loading={saveMutation.isLoading}
          onSubmit={saveMutation.mutate}
          onCancel={() => setModal({ open: false })}
        />
      </Modal>

      <Modal isOpen={importOpen} onClose={() => setImportOpen(false)} title="Import class timetable">
        <ClassImportForm
          loading={bulkMutation.isLoading}
          onImport={bulkMutation.mutate}
          onCancel={() => setImportOpen(false)}
          courses={courses}
          rooms={rooms}
          slots={slots}
          years={years}
          defaultYearId={selectedYear}
        />
      </Modal>
    </div>
  );
}

/* ─── Form ───────────────────────────────────────────────────────────── */
function ClassTimetableForm({ initial, years, courses, rooms, slots, defaultYearId, loading, onSubmit, onCancel }) {
  const [form, setForm] = useState({
    academicYearId: initial?.academic_year_id || defaultYearId || '',
    courseId:       initial?.course_id        || '',
    roomId:         initial?.room_id          || '',
    timeSlotId:     initial?.time_slot_id     || '',
    dayOfWeek:      initial?.day_of_week      || 'Monday',
    lecturerName:   initial?.lecturer_name    || '',
    notes:          initial?.notes            || '',
  });
  const set = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

  return (
    <form
      onSubmit={e => { e.preventDefault(); onSubmit(form); }}
      className="space-y-4 pt-1"
    >
      <div>
        <label className={labelCls}>Academic year *</label>
        <select required value={form.academicYearId} onChange={set('academicYearId')} className={inputCls}>
          <option value="">Select year</option>
          {years.map(y => <option key={y.id} value={y.id}>{y.name}</option>)}
        </select>
      </div>

      <div>
        <label className={labelCls}>Course *</label>
        <select required value={form.courseId} onChange={set('courseId')} className={inputCls}>
          <option value="">Select course</option>
          {courses.map(c => <option key={c.id} value={c.id}>{c.code} — {c.name}</option>)}
        </select>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>Day *</label>
          <select required value={form.dayOfWeek} onChange={set('dayOfWeek')} className={inputCls}>
            {DAYS.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>Time slot *</label>
          <select required value={form.timeSlotId} onChange={set('timeSlotId')} className={inputCls}>
            <option value="">Select slot</option>
            {slots.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
        </div>
      </div>

      <div>
        <label className={labelCls}>Room *</label>
        <select required value={form.roomId} onChange={set('roomId')} className={inputCls}>
          <option value="">Select room</option>
          {rooms.map(r => (
            <option key={r.id} value={r.id}>
              {r.name}{r.building ? ` (${r.building})` : ''}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className={labelCls}>Lecturer name</label>
        <input
          value={form.lecturerName}
          onChange={set('lecturerName')}
          className={inputCls}
          placeholder="Optional"
        />
      </div>

      <div className="flex gap-3 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 py-2.5 bg-white border border-[#E2E0D8] hover:border-[#888780]
                     text-[#5F5E5A] hover:text-[#1a1a18] rounded-xl transition-colors
                     text-[13px] font-medium"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading}
          className="flex-1 py-2.5 bg-[#1a1a18] hover:bg-[#2C2C2A] disabled:opacity-50
                     text-[#F7F6F2] rounded-xl transition-colors text-[13px] font-medium"
        >
          {loading ? 'Saving…' : 'Save'}
        </button>
      </div>
    </form>
  );
}

/* ─── Import Form ────────────────────────────────────────────────────── */
function ClassImportForm({ loading, onImport, onCancel, courses, rooms, slots, years, defaultYearId }) {
  const fileRef = useRef(null);
  const [rows, setRows] = useState(null);
  const [errors, setErrors] = useState([]);
  const [fileName, setFileName] = useState('');
  const [dragging, setDragging] = useState(false);
  const [showPreview, setShowPreview] = useState(true);
  const [fallbackYearId, setFallbackYearId] = useState(defaultYearId || '');

  const courseMap = Object.fromEntries(courses.map(c => [c.code.toLowerCase(), c.id]));
  const roomMap   = Object.fromEntries(rooms.map(r => [r.name.toLowerCase(), r.id]));
  const slotMap   = Object.fromEntries(slots.map(s => [s.label.toLowerCase(), s.id]));
  const yearMap   = Object.fromEntries(years.map(y => [y.name.toLowerCase(), y.id]));

  const normaliseDay = (raw) => {
    const match = DAYS.find(d => d.toLowerCase() === raw.trim().toLowerCase());
    return match || null;
  };

  const parseFile = useCallback((file) => {
    if (!file) return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: 'binary' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const raw = XLSX.utils.sheet_to_json(ws, { defval: '' });
        if (raw.length === 0) {
          setErrors([{ row: '-', message: 'File is empty or has no data rows.' }]);
          setRows(null); return;
        }
        const normalized = raw.map(r =>
          Object.fromEntries(
            Object.entries(r).map(([k, v]) => [k.trim().toLowerCase().replace(/\s+/g, '_'), String(v).trim()])
          )
        );
        const validRows = [], parseErrors = [];
        normalized.forEach((r, i) => {
          const rowNum = i + 2;
          const missing = REQUIRED_IMPORT_COLS.filter(col => !r[col]);
          if (missing.length) { parseErrors.push({ row: rowNum, message: `Missing: ${missing.join(', ')}` }); return; }

          const courseId = courseMap[r.course_code.toLowerCase()];
          if (!courseId) { parseErrors.push({ row: rowNum, message: `Course "${r.course_code}" not found` }); return; }

          const roomId = roomMap[r.room_name.toLowerCase()];
          if (!roomId) { parseErrors.push({ row: rowNum, message: `Room "${r.room_name}" not found` }); return; }

          const timeSlotId = slotMap[r.time_slot_label.toLowerCase()];
          if (!timeSlotId) { parseErrors.push({ row: rowNum, message: `Time slot "${r.time_slot_label}" not found` }); return; }

          const dayOfWeek = normaliseDay(r.day_of_week);
          if (!dayOfWeek) { parseErrors.push({ row: rowNum, message: `Invalid day_of_week "${r.day_of_week}"` }); return; }

          let academicYearId = fallbackYearId;
          if (r.academic_year_name) {
            const fromFile = yearMap[r.academic_year_name.toLowerCase()];
            if (!fromFile) { parseErrors.push({ row: rowNum, message: `Year "${r.academic_year_name}" not found` }); return; }
            academicYearId = fromFile;
          }
          if (!academicYearId) { parseErrors.push({ row: rowNum, message: 'No academic year specified' }); return; }

          validRows.push({
            courseId, roomId, timeSlotId, academicYearId,
            dayOfWeek, lecturerName: r.lecturer_name || '', notes: r.notes || '',
            _course: r.course_code, _room: r.room_name,
            _slot: r.time_slot_label, _day: dayOfWeek,
            _lecturer: r.lecturer_name || '',
          });
        });
        setRows(validRows.length > 0 ? validRows : null);
        setErrors(parseErrors);
      } catch {
        setErrors([{ row: '-', message: 'Could not parse file. Use a valid .xlsx or .csv.' }]);
        setRows(null);
      }
    };
    reader.readAsBinaryString(file);
  }, [courseMap, roomMap, slotMap, yearMap, fallbackYearId]);

  const handleReset = () => {
    setRows(null); setErrors([]); setFileName('');
    if (fileRef.current) fileRef.current.value = '';
  };

  const downloadTemplate = () => {
    const ws = XLSX.utils.aoa_to_sheet([
      ALL_IMPORT_COLS,
      ['CS101', 'Monday', '08:00 - 10:00', 'Hall A', 'Dr. Smith', '2024/2025', ''],
      ['MATH201', 'Wednesday', '10:15 - 12:15', 'Room 101', '', '', 'Bring calculators'],
    ]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Class Timetable');
    XLSX.writeFile(wb, 'class_timetable_template.xlsx');
  };

  const cleanRows = r => ({
    courseId: r.courseId, roomId: r.roomId, timeSlotId: r.timeSlotId,
    academicYearId: r.academicYearId, dayOfWeek: r.dayOfWeek,
    lecturerName: r.lecturerName, notes: r.notes,
  });

  const hasErrors = errors.length > 0;
  const canImport = rows && rows.length > 0;

  return (
    <div className="space-y-4 pt-1">

      {/* Instructions */}
      <div className="bg-[#F7F6F2] border border-[#E2E0D8] rounded-xl p-4 space-y-2.5">
        <p className="text-[12px] font-medium text-[#1a1a18]">Expected columns</p>
        <div className="flex flex-wrap gap-1.5">
          {ALL_IMPORT_COLS.map(col => (
            <span key={col}
              className="px-2 py-0.5 rounded-lg font-mono text-[11px]"
              style={REQUIRED_IMPORT_COLS.includes(col)
                ? { background: '#E6F1FB', color: '#185FA5' }
                : { background: '#F1EFE8', color: '#888780' }}
            >
              {col}{REQUIRED_IMPORT_COLS.includes(col) ? ' *' : ''}
            </span>
          ))}
        </div>
        <p className="text-[11px] text-[#B4B2A9]">
          * Required. Values must match existing courses, rooms, and time slots exactly.
          Day must be a full name e.g. <span className="font-mono">Monday</span>.
        </p>
        <button onClick={downloadTemplate}
          className="text-[11px] text-[#185FA5] hover:underline transition-colors">
          Download template (.xlsx)
        </button>
      </div>

      {/* Fallback year */}
      <div>
        <label className={labelCls}>
          Default academic year{' '}
          <span className="normal-case text-[#B4B2A9] font-normal">(used when not specified in file)</span>
        </label>
        <select value={fallbackYearId} onChange={e => setFallbackYearId(e.target.value)} className={inputCls}>
          <option value="">— None —</option>
          {years.map(y => <option key={y.id} value={y.id}>{y.name}{y.is_active ? ' · Active' : ''}</option>)}
        </select>
      </div>

      {/* Drop zone / file chip */}
      {!fileName ? (
        <div
          onDragOver={e => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={e => { e.preventDefault(); setDragging(false); parseFile(e.dataTransfer.files[0]); }}
          onClick={() => fileRef.current?.click()}
          className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors
            ${dragging ? 'border-[#185FA5] bg-[#E6F1FB]/40' : 'border-[#E2E0D8] hover:border-[#C8C6BE] hover:bg-[#FAFAF8]'}`}
        >
          <FileSpreadsheet className="w-7 h-7 text-[#C8C6BE] mx-auto mb-3" />
          <p className="text-[13px] text-[#1a1a18] font-medium">
            Drop your file here, or <span className="text-[#185FA5]">browse</span>
          </p>
          <p className="text-[11px] text-[#B4B2A9] mt-1">.xlsx or .csv</p>
          <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv"
            onChange={e => parseFile(e.target.files[0])} className="hidden" />
        </div>
      ) : (
        <div className="flex items-center justify-between bg-[#F7F6F2] border border-[#E2E0D8] rounded-xl px-4 py-3">
          <div className="flex items-center gap-3 min-w-0">
            <FileSpreadsheet className="w-4 h-4 text-[#185FA5] flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-[13px] font-medium text-[#1a1a18] truncate">{fileName}</p>
              <p className="text-[11px] text-[#888780]">
                {rows ? `${rows.length} valid row(s)` : '0 valid rows'}
                {hasErrors ? ` · ${errors.length} error(s)` : ''}
              </p>
            </div>
          </div>
          <button onClick={handleReset}
            className="p-1.5 text-[#B4B2A9] hover:text-[#1a1a18] hover:bg-[#E2E0D8] rounded-lg transition-colors flex-shrink-0 ml-2">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* Errors */}
      {hasErrors && (
        <div className="bg-[#FCEBEB] border border-[#F09595] rounded-xl p-3 space-y-1.5">
          <p className="text-[11px] font-medium text-[#A32D2D] uppercase tracking-wider flex items-center gap-1.5">
            <AlertCircle className="w-3 h-3" /> {errors.length} row(s) skipped
          </p>
          <ul className="space-y-0.5 max-h-28 overflow-y-auto">
            {errors.map((err, i) => (
              <li key={i} className="text-[11px] text-[#791F1F]">Row {err.row}: {err.message}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Preview */}
      {rows && rows.length > 0 && (
        <div>
          <button onClick={() => setShowPreview(p => !p)}
            className="flex items-center gap-1.5 text-[11px] font-medium text-[#888780]
                       hover:text-[#1a1a18] transition-colors mb-2">
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showPreview ? '' : '-rotate-90'}`} />
            Preview ({rows.length} rows)
          </button>
          {showPreview && (
            <div className="border border-[#E2E0D8] rounded-xl overflow-hidden max-h-52 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-[#F7F6F2] border-b border-[#E2E0D8]">
                  <tr>
                    {['Course', 'Day', 'Slot', 'Room', 'Lecturer'].map(h => (
                      <th key={h} className="text-left text-[10px] font-medium text-[#888780]
                                             uppercase tracking-wider px-3 py-2">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i} className={`${i < rows.length - 1 ? 'border-b border-[#F1EFE8]' : ''} hover:bg-[#FAFAF8]`}>
                      <td className="px-3 py-2 font-mono text-[#185FA5]">{r._course}</td>
                      <td className="px-3 py-2">
                        <DayPill day={r._day} />
                      </td>
                      <td className="px-3 py-2 text-[#888780]">{r._slot}</td>
                      <td className="px-3 py-2 text-[#888780]">{r._room}</td>
                      <td className="px-3 py-2 text-[#888780]">
                        {r._lecturer || <span className="text-[#D3D1C7]">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Success */}
      {canImport && !hasErrors && (
        <div className="flex items-center gap-2 text-[13px] bg-[#EAF3DE] border border-[#C0DD97]
                        text-[#3B6D11] rounded-xl px-3 py-2.5">
          <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
          All {rows.length} rows validated successfully
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-3 pt-1">
        <button type="button" onClick={onCancel}
          className="flex-1 py-2.5 bg-white border border-[#E2E0D8] hover:border-[#888780]
                     text-[#5F5E5A] hover:text-[#1a1a18] rounded-xl transition-colors text-[13px] font-medium">
          Cancel
        </button>
        <button
          onClick={() => canImport && onImport(rows.map(cleanRows))}
          disabled={!canImport || loading}
          className="flex-1 py-2.5 bg-[#1a1a18] hover:bg-[#2C2C2A] disabled:opacity-40
                     disabled:cursor-not-allowed text-[#F7F6F2] rounded-xl transition-colors text-[13px] font-medium">
          {loading ? 'Importing…' : `Import ${rows?.length ?? 0} class(es)`}
        </button>
      </div>
    </div>
  );
}